<template>
  <v-app>
    <v-container class="print-container">
      <div class="content" v-if="client && !loading">
        <!-- Header -->
        <v-row class="mb-6 mt-4" justify="center">
          <v-col cols="12" class="text-center">
            <span class="company-name-badge">{{ this.$store.getters.companyName }}</span>
            <h2 class="report-title mt-4">كشف حركة محفظة العميل</h2>
          </v-col>
        </v-row>

        <!-- Client & Filter Info Grid -->
        <div class="info-grid mb-6">
          <v-row dense>
            <v-col cols="6" class="text-right">
              <div class="info-item">
                <span class="info-label">اسم العميل:</span>
                <span class="info-value font-weight-bold">{{ client.name }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">رقم الجوال:</span>
                <span class="info-value">{{ client.phoneNumber }}</span>
              </div>
              <div class="info-item" v-if="client.branch">
                <span class="info-label">الفرع:</span>
                <span class="info-value">{{ client.branch.name }}</span>
              </div>
            </v-col>
            <v-col cols="6" class="text-right">
              <div class="info-item">
                <span class="info-label">تاريخ الطباعة:</span>
                <span class="info-value">{{ printDate }} - {{ printTime }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">الرصيد الحالي للمحفظة:</span>
                <span class="info-value font-weight-bold primary--text">{{ client.walletBalance || 0 }} ر.س</span>
              </div>
              <div class="info-item" v-if="startDate || endDate">
                <span class="info-label">فترة الفلترة:</span>
                <span class="info-value">
                  <span v-if="startDate">من: {{ startDate }}</span>
                  <span v-if="endDate"> إلى: {{ endDate }}</span>
                </span>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Transactions Table -->
        <table class="report-table mb-6">
          <thead>
            <tr>
              <th style="width: 8%">م</th>
              <th style="width: 12%">رقم الحركة</th>
              <th style="width: 18%">التاريخ</th>
              <th style="width: 12%">النوع</th>
              <th style="width: 15%">المبلغ</th>
              <th style="width: 15%">المرجع</th>
              <th>البيان / الملاحظات</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in transactions" :key="item.id">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-center">{{ item.id }}</td>
              <td class="text-center">{{ formateDateWithMoment(item.timestamp, 'YYYY-MM-DD HH:mm') }}</td>
              <td class="text-center">
                <span :class="item.type === 1 ? 'success-text' : 'error-text'">
                  {{ item.type === 1 ? 'إيداع' : 'سحب' }}
                </span>
              </td>
              <td class="text-right font-weight-bold" :class="item.type === 1 ? 'success-text' : 'error-text'">
                {{ item.type === 1 ? '+' : '-' }}{{ item.amount }} ر.س
              </td>
              <td>{{ item.reference || '-' }}</td>
              <td>{{ item.notice }}</td>
            </tr>
            <tr v-if="transactions.length === 0">
              <td colspan="7" class="text-center grey--text py-4">لا توجد معاملات مسجلة في هذه الفترة.</td>
            </tr>
          </tbody>
        </table>

        <!-- Summary Totals Section -->
        <div class="summary-section mt-4">
          <v-row justify="end">
            <v-col cols="6" sm="4">
              <table class="summary-table">
                <tbody>
                  <tr>
                    <th>إجمالي الإيداعات (+)</th>
                    <td class="success-text font-weight-bold">{{ totalDeposits }} ر.س</td>
                  </tr>
                  <tr>
                    <th>إجمالي السحوبات (-)</th>
                    <td class="error-text font-weight-bold">{{ totalWithdrawals }} ر.س</td>
                  </tr>
                  <tr>
                    <th>صافي الحركة</th>
                    <td class="font-weight-bold" :class="netAmount >= 0 ? 'success-text' : 'error-text'">
                      {{ netAmount >= 0 ? '+' : '' }}{{ netAmount }} ر.س
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-col>
          </v-row>
        </div>

        <!-- Signatures -->
        <v-row class="mt-12 pt-6">
          <v-col cols="6" class="text-center">
            <h4 class="signature-title">المحاسب</h4>
            <div class="signature-line mt-10" />
          </v-col>
          <v-col cols="6" class="text-center">
            <h4 class="signature-title">المدير المالي</h4>
            <div class="signature-line mt-10" />
          </v-col>
        </v-row>
      </div>

      <div v-else class="text-center loading-container py-12">
        <v-progress-circular indeterminate color="primary" size="64" class="mb-4" />
        <div class="text-h6">جاري تحميل البيانات وتجهيز الكشف للطباعة...</div>
      </div>
    </v-container>
  </v-app>
</template>

<script>
import ClientsService from "../../LandShipping/services/ClientsService.js";
import WalletService from "../../Finances/services/ClientsWalletService.js";

export default {
  name: "ClientWalletTransactionsPrint",
  data() {
    return {
      loading: true,
      client: null,
      transactions: [],
      clientId: null,
      startDate: "",
      endDate: "",
      printDate: this.formateDateWithMoment(Date.now(), "YYYY-MM-DD"),
      printTime: new Date().toLocaleTimeString("ar-EG", { hour: '2-digit', minute: '2-digit' }),
    };
  },
  computed: {
    totalDeposits() {
      return this.transactions
        .filter(t => t.type === 1)
        .reduce((sum, t) => sum + t.amount, 0);
    },
    totalWithdrawals() {
      return this.transactions
        .filter(t => t.type === 2)
        .reduce((sum, t) => sum + t.amount, 0);
    },
    netAmount() {
      return this.totalDeposits - this.totalWithdrawals;
    }
  },
  mounted() {
    this.clientId = +this.$route.query.clientId;
    this.startDate = this.$route.query.startDate || "";
    this.endDate = this.$route.query.endDate || "";

    if (this.clientId) {
      this.fetchData();
    } else {
      this.loading = false;
      this.$dialog.notify.error("حدث خطأ: لم يتم تحديد عميل");
    }
  },
  methods: {
    fetchData() {
      this.loading = true;
      // Fetch Client details
      const clientPromise = ClientsService.byId(this.clientId)
        .then((resp) => {
          this.client = resp.data;
        });

      // Fetch all matching transactions (setting page size extremely large to get all items for print)
      const transactionsPromise = WalletService.query({
        page: 1,
        itemsPerPage: 100000,
        clientId: this.clientId,
        startDate: this.startDate,
        endDate: this.endDate,
      }).then((resp) => {
        this.transactions = resp.data.items;
      });

      Promise.all([clientPromise, transactionsPromise])
        .then(() => {
          this.loading = false;
          this.triggerPrint();
        })
        .catch((err) => {
          this.loading = false;
          this.$dialog.notify.error("حدث خطأ أثناء تحميل بيانات كشف المحفظة");
        });
    },
    triggerPrint() {
      setTimeout(() => {
        window.focus();
        const css = "@page { size: landscape; margin: 15mm; }";
        const head = document.head || document.getElementsByTagName("head")[0];
        const style = document.createElement("style");
        style.type = "text/css";
        style.media = "print";
        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(document.createTextNode(css));
        }
        head.appendChild(style);

        // Set document title to something clean for the PDF print filename
        document.title = `كشف_محفظة_${this.client.name.replace(/\s+/g, '_')}_${this.printDate}`;

        window.print();
      }, 300);
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  -webkit-print-color-adjust: exact !important;
  color-adjust: exact !important;
}

.print-container {
  direction: rtl;
  font-family: 'Cairo', 'Inter', sans-serif;
  color: #333;
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
}

.company-name-badge {
  border: 2px solid #333;
  padding: 6px 30px;
  font-size: 24px;
  font-weight: bold;
  display: inline-block;
}

.report-title {
  font-size: 22px;
  font-weight: 800;
  color: #111;
  text-decoration: underline;
}

.info-grid {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  background-color: #fafafa;
  font-size: 14px;
}

.info-item {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.info-label {
  font-weight: bold;
  color: #666;
  min-width: 150px;
  text-align: right;
}

.info-value {
  color: #111;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 13px;

  th,
  td {
    border: 1px solid #666;
    padding: 8px 10px;
    text-align: right;
  }

  th {
    background-color: #eaeaea !important;
    color: #000;
    text-align: center;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #fafafa;
  }
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    border: 1px solid #666;
    padding: 8px 12px;
  }

  th {
    background-color: #f5f5f5 !important;
    text-align: right;
    font-weight: bold;
    width: 60%;
  }

  td {
    text-align: left;
  }
}

.success-text {
  color: #2e7d32 !important;
}

.error-text {
  color: #c62828 !important;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.signature-title {
  font-size: 16px;
  font-weight: bold;
}

.signature-line {
  width: 150px;
  border-bottom: 1px dashed #666;
  margin: 0 auto;
}

@media print {
  body {
    background-color: #fff;
  }

  .v-application {
    background: none !important;
  }

  .print-container {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .report-table {
    width: 100% !important;
  }

  .report-table tr {
    page-break-inside: avoid !important;
  }
}
</style>
